import React, { Fragment, useState } from 'react'
import toast from 'react-hot-toast'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import styles from './ManageHappyFoxHelpdesk.module.css'

import Heading from 'Design/components/Heading'
import Label from 'Design/components/Label'
import Input from 'Design/components/Input'
import Link from 'Design/components/Link'
import Switch from 'Design/components/Switch'
import Select from 'Design/components/Select'
import Button from 'Design/components/Button'

import { useAppDetailContext } from '../../AppDetailContext'
import { useGetCategoriesQuery, useUninstallMutation, useUpdateSettingsMutation } from './happyfox-helpdesk.service'
import UnInstallApp from '../../../components/UnInstallApp'
import FormControl from 'Design/components/FormControl'
import ConfirmationModal from 'Design/components/ConfirmationModal'

const ManageHappyFoxHelpdesk = (props) => {
  const { settings } = props

  const [showUninstallModal, setShowUninstallModal] = useState(false)

  const { name } = useAppDetailContext()

  const navigate = useNavigate()

  const [uninstall, uninstallResult] = useUninstallMutation()
  const [updateSettings, updateSettingsResult] = useUpdateSettingsMutation()
  const { data: categories } = useGetCategoriesQuery()

  const { control, register, watch, handleSubmit } = useForm({
    defaultValues: {
      createTicket: settings.createTicket ?? false,
      ticketCategory: settings.category ?? 1
    }
  })

  const createTicket = watch('createTicket')

  const handleFormSubmit = (data) => {
    const payload = {
      createTicket: data.createTicket,
      ticketCategory: data.ticketCategory
    }

    const promise = updateSettings(payload).unwrap()

    toast.promise(promise, {
      loading: `Updating settings for ${name}`,
      success: `Sucessfully updated settings for ${name}`,
      error: `Unable to update ${name} settings. Try again`
    })
  }

  const handleUninstall = async () => {
    const promise = uninstall().unwrap()
    toast.promise(promise, {
      loading: `Uninstalling ${name}`,
      success: `${name} integration uninstalled successfully`,
      error: `Unable to uninstall ${name}. Try again`
    })

    promise.then(handleCloseModal)
  }

  const handleCloseModal = () => {
    setShowUninstallModal(false)
  }

  const handleOpenModal = () => {
    setShowUninstallModal(true)
  }

  const handleChangeAuthClick = () => {
    navigate('auth')
  }

  const isUninstalling = uninstallResult.isLoading

  return (
    <Fragment>
      <ConfirmationModal
        isOpen={showUninstallModal}
        title='Uninstall Happyfox Helpdesk'
        description='Are you sure to uninstall this app?'
        onCloseButtonClick={handleCloseModal}
        onConfirmButtonClick={handleUninstall}
        confirmButtonText='Yes'
        cancelButtonText='No'
        disableConfirmButton={isUninstalling}
      />
      <Heading level={2}>Manage {name}</Heading>
      <section>
        <Label className={styles.AccountLabel}>
          You have successfully connected your HappyFox Help Desk account
          <Link role='button' className={styles.ChangeButton} onClick={handleChangeAuthClick}>
            Change
          </Link>
        </Label>
        <Input value={settings.account_url} readOnly />
      </section>
      <form className={styles.SettingsContainer} onSubmit={handleSubmit(handleFormSubmit)}>
        <Heading level={2}>Settings</Heading>

        <div className={styles.Setting}>
          <div className={styles.SwitchContainer}>
            Create tickets in helpdesk after each conversation
            <div className={styles.SettingSwitch}>
              <Controller
                control={control}
                name='createTicket'
                render={({ field: { value, onChange, ref } }) => {
                  return <Switch checked={value} onChange={onChange} ref={ref} />
                }}
              />
            </div>
          </div>

          {createTicket && categories && (
            <FormControl>
              <Label>Select Category</Label>
              <Select {...register('ticketCategory', { shouldUnregister: true, valueAsNumber: true })}>
                {categories.map((category, index) => {
                  return (
                    <option key={`${category.name}__${category.id}-${index}`} value={category.id}>
                      {category.name}
                    </option>
                  )
                })}
              </Select>
            </FormControl>
          )}
        </div>
        <Button type='submit' variant='primary' className={styles.SaveButton} disabled={updateSettingsResult.isLoading}>
          Save
        </Button>
      </form>

      <UnInstallApp onUninstall={handleOpenModal} isLoading={isUninstalling} />
    </Fragment>
  )
}

export default ManageHappyFoxHelpdesk
